import { REGISTRATION_STATE, RegistrationStateStore } from "@features/client/registration-state"
import { APPLICATION_STATE, ApplicationStateStore } from "@features/loan-application/application-state"
import { APP_ROUTE_NAMES, FLOW_ROUTES } from "@app/app.routes"
import { CLIENT_FLOW_ROUTE_NAMES } from "@pages/client-registration-flow/routes"
import { LOAN_APPLICATION_ROUTE_NAMES } from "@pages/loan-application-flow/routes"
import { PROFILE_ROUTE_NAMES } from "@pages/profile/routes"
import { inject, Injectable } from "@angular/core"
import { LoanApplicationService } from "@features/loan-application/loan-application.service"
import { catchError, map, Observable, of, switchMap, take } from "rxjs"
import { LeadService } from "@features/lead/lead.service"
import { AuthService } from "@features/auth/auth.service"

type FlowState = REGISTRATION_STATE | APPLICATION_STATE
type NavigationCommands =
  [APP_ROUTE_NAMES.FLOW, FLOW_ROUTES.REGISTRATION, CLIENT_FLOW_ROUTE_NAMES]
  | [APP_ROUTE_NAMES.PROFILE, PROFILE_ROUTE_NAMES]
  | [APP_ROUTE_NAMES.FLOW, FLOW_ROUTES.LOAN, LOAN_APPLICATION_ROUTE_NAMES]
  | [APP_ROUTE_NAMES.LEAD, string]

const flowRoutes: Record<FlowState, NavigationCommands> = {
  [REGISTRATION_STATE.NO_CLIENT]: [APP_ROUTE_NAMES.FLOW, FLOW_ROUTES.REGISTRATION, CLIENT_FLOW_ROUTE_NAMES.REGISTRATION],
  [REGISTRATION_STATE.ADDITIONAL_INFO]: [APP_ROUTE_NAMES.FLOW, FLOW_ROUTES.REGISTRATION, CLIENT_FLOW_ROUTE_NAMES.ADDITIONAL_INFO],
  [REGISTRATION_STATE.IDENTITY_VERIFICATION]: [APP_ROUTE_NAMES.FLOW, FLOW_ROUTES.REGISTRATION, CLIENT_FLOW_ROUTE_NAMES.VERIFICATION],
  [REGISTRATION_STATE.DISBURSEMENT_DATA]: [APP_ROUTE_NAMES.FLOW, FLOW_ROUTES.REGISTRATION, CLIENT_FLOW_ROUTE_NAMES.DISBURSEMENT],
  [APPLICATION_STATE.NEW_APPLICATION]: [APP_ROUTE_NAMES.FLOW, FLOW_ROUTES.LOAN, LOAN_APPLICATION_ROUTE_NAMES.SUMMARY],
  [APPLICATION_STATE.PENDING_AGREEMENT]: [APP_ROUTE_NAMES.PROFILE, PROFILE_ROUTE_NAMES.LOAN_OVERVIEW],
  [APPLICATION_STATE.PENDING_REJECTION]: [APP_ROUTE_NAMES.PROFILE, PROFILE_ROUTE_NAMES.LOAN_OVERVIEW],
  [APPLICATION_STATE.AGREEMENT_PROMOTION]: [APP_ROUTE_NAMES.FLOW, FLOW_ROUTES.LOAN, LOAN_APPLICATION_ROUTE_NAMES.FAST_FEE],
  [APPLICATION_STATE.PENDING_DECISION]: [APP_ROUTE_NAMES.FLOW, FLOW_ROUTES.LOAN, LOAN_APPLICATION_ROUTE_NAMES.DECISION_PROGRESSION],
  [APPLICATION_STATE.OFFER_INCREASE]: [APP_ROUTE_NAMES.FLOW, FLOW_ROUTES.LOAN, LOAN_APPLICATION_ROUTE_NAMES.INCREASE_OFFER],
  [APPLICATION_STATE.OFFER_DECREASE]: [APP_ROUTE_NAMES.FLOW, FLOW_ROUTES.LOAN, LOAN_APPLICATION_ROUTE_NAMES.DECREASE_OFFER],
  [APPLICATION_STATE.BANK_STATEMENT_SUBMISSION]: [APP_ROUTE_NAMES.FLOW, FLOW_ROUTES.LOAN, LOAN_APPLICATION_ROUTE_NAMES.BANK_STATEMENT_SUBMISSION],
  [APPLICATION_STATE.BANK_STATEMENT_MANUAL_UPLOAD]: [APP_ROUTE_NAMES.FLOW, FLOW_ROUTES.LOAN, LOAN_APPLICATION_ROUTE_NAMES.BANK_STATEMENT_MANUAL_UPLOAD],
  [APPLICATION_STATE.BANK_STATEMENT_CHECK]: [APP_ROUTE_NAMES.FLOW, FLOW_ROUTES.LOAN, LOAN_APPLICATION_ROUTE_NAMES.BANK_STATEMENT_CHECK],
}

@Injectable({
  providedIn: 'root'
})
export class FlowRouteStateStore {
  #applications = inject(LoanApplicationService)
  #leads = inject(LeadService)
  #auth = inject(AuthService)

  #store = {
    registration: inject(RegistrationStateStore),
    application: inject(ApplicationStateStore),
  }

  /**
   * It is required to put lead landing page before everything else.
   * To do that right now, we need to ensure there is no pending preliminary or loan application.
   * Otherwise, in case of multiple active leads lookup might happen multiple times.
   * Only application state can get a proper state we can rely on, but it is decoupled from client state so logic is as follows:
   * Query application state if it states that new application can be created proceed with lead lookup procedure.
   * If there is no lead to lookup or app state was different query registration state if that one is empty proceed with previously retrieved application state
   */
  get(previousUrl: string): Observable<NavigationCommands> {
    // For non-authenticated user only possible outcome is registration page
    if (!this.#auth.authenticated) {
      return of([APP_ROUTE_NAMES.FLOW, FLOW_ROUTES.REGISTRATION, CLIENT_FLOW_ROUTE_NAMES.REGISTRATION])
    }

    const hasPreliminary$ = this.#applications.preliminary$.pipe(map(Boolean))
    const leadLookup$ = hasPreliminary$.pipe(
      switchMap(hasPreliminary => hasPreliminary ? of(null) : this.#leads.lookup()),
      catchError(() => of(null)),
      map(l => l?.id ?? null),
    )

    const mapRouteState = map((state: FlowState) => {
      // if we just finished application flow, we want to first show the profile
      if (state === APPLICATION_STATE.NEW_APPLICATION && previousUrl.includes(FLOW_ROUTES.LOAN)) {
        return [APP_ROUTE_NAMES.PROFILE, PROFILE_ROUTE_NAMES.LOAN_OVERVIEW] as NavigationCommands
      }

      return flowRoutes[state]
    })

    const registrationState$ = this.#store.registration.get()
    const applicationState$ = this.#store.application.get()

    return applicationState$.pipe(
      switchMap(state => {
        const nextRoute$ = registrationState$.pipe(map(registrationState => registrationState ?? state)).pipe(mapRouteState)

        if (state === APPLICATION_STATE.NEW_APPLICATION) {
          return leadLookup$.pipe(
            switchMap(leadId => leadId ? of([APP_ROUTE_NAMES.LEAD, leadId] as NavigationCommands) : nextRoute$)
          )
        }

        return nextRoute$
      }),
      take(1),
    )
  }
}
